/**
 * Stage 1A-d — client for the worker's structured reports ("I am late",
 * "cannot find the site", "work stopped", "I worked but did not register"…).
 *
 * `reportWorkException` writes a structured message into the worker's own
 * RM24 thread and pushes staff. It never changes hours or money. Every call
 * carries a `clientRequestId` that the server turns into the message id, so a
 * retry answers with the same message instead of a second one.
 *
 * The worker UI calls `reportWorkExceptionQueued` (below), which also
 * survives no signal; the plain `reportWorkException` is what the queue
 * replays.
 */

import type {
  ReportWorkExceptionInput,
  ReportWorkExceptionResult,
} from '@rendimees24/shared-types';

import { httpsCallableWithLog as httpsCallable } from './errorLog';
import { getFirebase } from './firebase';
// Circular on purpose and safe: syncQueue only calls back into this module
// from inside its executors, never while either module is initialising.
import { reportWorkExceptionViaQueue } from './syncQueue';

export async function reportWorkException(
  input: ReportWorkExceptionInput,
): Promise<ReportWorkExceptionResult> {
  const { functions } = getFirebase();
  const callable = httpsCallable<ReportWorkExceptionInput, ReportWorkExceptionResult>(
    functions,
    'reportWorkException',
  );
  const { data } = await callable(input);
  return data;
}

/** A fresh id for one tap. Reuse it for every retry of that tap. */
export function newClientRequestId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

export interface ReportWorkExceptionQueuedInput {
  kind: ReportWorkExceptionInput['kind'];
  jobId: string;
  minutesLate?: number;
  note?: string;
  /** `missed_day`: the local date worked, YYYY-MM-DD. */
  date?: string;
  /** `missed_day`: local start "HH:MM" (alias of `startTime`). */
  time?: string;
  startTime?: string;
  endTime?: string;
  /** Pass the same id when the worker retries the same tap; else one is made. */
  clientRequestId?: string;
}

/**
 * What the worker UI calls. `sent` — the server has it (RM24 knows);
 * `queued` — it is still on the device and goes when the signal returns
 * (say "waiting to be sent", never "sent"). A refusal (invalid input,
 * NOT_ON_JOB, NOT_FIXED_PRICE, JOB_INVOICED, MISSED_DAY_IN_FUTURE) is thrown.
 */
export async function reportWorkExceptionQueued(
  input: ReportWorkExceptionQueuedInput,
): Promise<{ kind: 'sent' | 'queued'; clientRequestId: string }> {
  const { time, startTime, ...rest } = input;
  const clientRequestId = input.clientRequestId ?? newClientRequestId();
  const start = startTime ?? time;
  const r = await reportWorkExceptionViaQueue({
    ...rest,
    ...(start ? { startTime: start } : {}),
    clientRequestId,
  });
  return { kind: r.queued ? 'queued' : 'sent', clientRequestId };
}
